import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Truck, 
  Plus, 
  Edit, 
  Trash2, 
  Star, 
  MapPin, 
  Clock, 
  Euro, 
  Gift,
  Check,
  AlertCircle,
  Loader2,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Delivery area options - predisposed for future expansion
const DELIVERY_AREAS = [
  { value: 'italy', label: 'Italia', icon: '🇮🇹', description: 'Tutto il territorio italiano' },
  // Future expansion:
  // { value: 'europe', label: 'Europa', icon: '🇪🇺', description: 'Unione Europea' },
  // { value: 'world', label: 'Mondo', icon: '🌍', description: 'Spedizione internazionale' },
];

export default function ShippingRulesManager({ crafterId, onRulesChange }) {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    delivery_days_min: '1',
    delivery_days_max: '3',
    delivery_area: 'italy',
    free_shipping_threshold: '',
    is_default: false,
    is_active: true,
    combine_shipping: true // Default: accorpa spedizioni per acquisti multipli
  });
  
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (crafterId) {
      loadRules();
    }
  }, [crafterId]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/shipping-rules/${crafterId}`);
      if (response.ok) {
        const data = await response.json();
        setRules(data.rules || []);
        if (onRulesChange) onRulesChange(data.rules || []);
      }
    } catch (error) {
      console.error('Error loading shipping rules:', error);
      toast.error('Errore nel caricamento delle regole di spedizione');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      delivery_days_min: '1',
      delivery_days_max: '3',
      delivery_area: 'italy',
      free_shipping_threshold: '',
      is_default: rules.length === 0, // First rule is always default
      is_active: true,
      combine_shipping: true // Default: accorpa spedizioni
    });
    setFormErrors({});
    setEditingRule(null);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      price: rule.price.toString(),
      delivery_days_min: rule.delivery_days_min.toString(),
      delivery_days_max: rule.delivery_days_max.toString(),
      delivery_area: rule.delivery_area,
      free_shipping_threshold: rule.free_shipping_threshold?.toString() || '',
      is_default: rule.is_default,
      is_active: rule.is_active,
      combine_shipping: rule.combine_shipping !== false // Default true if not set
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Il nome è obbligatorio';
    }
    
    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      errors.price = 'Inserisci un prezzo valido (minimo 0€ per spedizione gratuita)';
    }
    
    const daysMin = parseInt(formData.delivery_days_min);
    const daysMax = parseInt(formData.delivery_days_max);
    if (isNaN(daysMin) || daysMin < 1) {
      errors.delivery_days_min = 'Minimo 1 giorno';
    }
    if (isNaN(daysMax) || daysMax < daysMin) {
      errors.delivery_days_max = 'Deve essere maggiore o uguale ai giorni minimi';
    }
    
    if (formData.free_shipping_threshold) {
      const threshold = parseFloat(formData.free_shipping_threshold);
      if (isNaN(threshold) || threshold <= 0) {
        errors.free_shipping_threshold = 'Inserisci un importo valido (maggiore di 0)';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const ruleData = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        delivery_days_min: parseInt(formData.delivery_days_min),
        delivery_days_max: parseInt(formData.delivery_days_max),
        delivery_area: formData.delivery_area,
        free_shipping_threshold: formData.free_shipping_threshold ? parseFloat(formData.free_shipping_threshold) : null,
        is_default: formData.is_default,
        is_active: formData.is_active,
        combine_shipping: formData.combine_shipping
      };
      
      let response;
      if (editingRule) {
        response = await fetch(`${API_URL}/api/shipping-rules/${editingRule.id}?crafter_id=${crafterId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ruleData)
        });
      } else {
        response = await fetch(`${API_URL}/api/shipping-rules?crafter_id=${crafterId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ruleData)
        });
      }
      
      if (response.ok) {
        toast.success(editingRule ? 'Regola aggiornata!' : 'Regola creata!');
        setDialogOpen(false);
        resetForm();
        await loadRules();

        // Se l'utente è arrivato qui dal flow di creazione prodotto,
        // riportalo automaticamente alla home dashboard dove il dialog
        // "Aggiungi creazione" verrà riaperto (auto-open via sessionStorage flag).
        const isCreatingNewRule = !editingRule;
        if (isCreatingNewRule && sessionStorage.getItem('pending_product_creation') === '1') {
          toast.info('Torno alla creazione del prodotto...');
          setTimeout(() => {
            navigate('/dashboard?tab=artigiano&section=home');
          }, 400);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Errore nel salvataggio della regola');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/shipping-rules/${ruleToDelete.id}?crafter_id=${crafterId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Regola eliminata');
        setDeleteDialogOpen(false);
        setRuleToDelete(null);
        await loadRules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Errore nell\'eliminazione della regola');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (ruleId) => {
    try {
      const response = await fetch(`${API_URL}/api/shipping-rules/${ruleId}/set-default?crafter_id=${crafterId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Regola impostata come predefinita');
        await loadRules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error('Errore nell\'impostazione');
    }
  };

  const formatDeliveryDays = (min, max) => {
    if (min === max) {
      return `${min} giorno lavorativo`;
    }
    return `${min}-${max} giorni lavorativi`;
  };

  const getAreaInfo = (areaValue) => {
    return DELIVERY_AREAS.find(a => a.value === areaValue) || DELIVERY_AREAS[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shipping-rules-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#008080]" />
            Regole di Spedizione
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Definisci le opzioni di spedizione per i tuoi prodotti
          </p>
        </div>
        <Button onClick={openCreateDialog} className="bg-[#008080] text-white" data-testid="add-shipping-rule-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuova Regola
        </Button>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card className="p-8 text-center border-dashed border-2">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h4 className="font-semibold text-slate-700 mb-2">Nessuna regola di spedizione</h4>
          <p className="text-sm text-slate-500 mb-4">
            Crea la tua prima regola di spedizione per poter pubblicare i prodotti
          </p>
          <Button onClick={openCreateDialog} className="bg-[#008080] text-white">
            <Plus className="w-4 h-4 mr-2" />
            Crea Prima Regola
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {rules.map((rule, index) => {
              const areaInfo = getAreaInfo(rule.delivery_area);
              return (
                <motion.div
                  key={rule.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={`p-4 transition-all hover:shadow-md ${
                      rule.is_default ? 'border-[#008080] bg-[#008080]/5' : ''
                    } ${!rule.is_active ? 'opacity-50' : ''}`}
                    data-testid={`shipping-rule-${rule.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        rule.is_default ? 'bg-[#008080] text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <Truck className="w-6 h-6" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h4 className="font-semibold">{rule.name}</h4>
                          {rule.is_default && (
                            <Badge className="bg-[#008080] text-white text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Predefinita
                            </Badge>
                          )}
                          {!rule.is_active && (
                            <Badge variant="outline" className="text-slate-500 text-xs">
                              Disattivata
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 mt-2">
                          <span className="flex items-center gap-1 font-medium text-[#008080]">
                            <Euro className="w-4 h-4" />
                            €{rule.price.toFixed(2)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDeliveryDays(rule.delivery_days_min, rule.delivery_days_max)}
                          </span>
                          <span className="flex items-center gap-1">
                            <span>{areaInfo.icon}</span>
                            {areaInfo.label}
                          </span>
                          {rule.free_shipping_threshold && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Gift className="w-4 h-4" />
                              Gratis sopra €{rule.free_shipping_threshold}
                            </span>
                          )}
                          {rule.combine_shipping !== false && (
                            <span className="flex items-center gap-1 text-amber-600" title="Spedizione unica per acquisti multipli">
                              <Package className="w-4 h-4" />
                              Accorpata
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {!rule.is_default && rule.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefault(rule.id)}
                            className="text-[#008080] hover:bg-[#008080]/10"
                            title="Imposta come predefinita"
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(rule)}
                          className="text-slate-600 hover:text-slate-800"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!rule.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRuleToDelete(rule);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Info box */}
      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Come funzionano le regole di spedizione?</p>
            <ul className="list-disc list-inside space-y-1 text-amber-700">
              <li>La regola <strong>predefinita</strong> viene applicata automaticamente ai nuovi prodotti</li>
              <li>Puoi assegnare regole diverse a prodotti specifici durante la creazione</li>
              <li>La soglia "spedizione gratuita" si calcola sul totale acquisti del cliente <strong>per artigiano</strong></li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#008080]" />
              {editingRule ? 'Modifica Regola' : 'Nuova Regola di Spedizione'}
            </DialogTitle>
            <DialogDescription>
              {editingRule 
                ? 'Modifica i dettagli della regola di spedizione'
                : 'Configura una nuova opzione di spedizione per i tuoi prodotti'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label htmlFor="rule-name">Nome regola *</Label>
              <Input
                id="rule-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Es: Spedizione Standard Italia"
                className={formErrors.name ? 'border-red-500' : ''}
              />
              {formErrors.name && (
                <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>
              )}
            </div>
            
            {/* Price */}
            <div>
              <Label htmlFor="rule-price">Prezzo spedizione (€) *</Label>
              <Input
                id="rule-price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="5.00"
                className={formErrors.price ? 'border-red-500' : ''}
              />
              {formErrors.price && (
                <p className="text-sm text-red-500 mt-1">{formErrors.price}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">Inserisci 0 per spedizione sempre gratuita</p>
            </div>
            
            {/* Delivery Days */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="days-min">Giorni minimi *</Label>
                <Input
                  id="days-min"
                  type="number"
                  min="1"
                  value={formData.delivery_days_min}
                  onChange={(e) => setFormData({ ...formData, delivery_days_min: e.target.value })}
                  className={formErrors.delivery_days_min ? 'border-red-500' : ''}
                />
                {formErrors.delivery_days_min && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.delivery_days_min}</p>
                )}
              </div>
              <div>
                <Label htmlFor="days-max">Giorni massimi *</Label>
                <Input
                  id="days-max"
                  type="number"
                  min="1"
                  value={formData.delivery_days_max}
                  onChange={(e) => setFormData({ ...formData, delivery_days_max: e.target.value })}
                  className={formErrors.delivery_days_max ? 'border-red-500' : ''}
                />
                {formErrors.delivery_days_max && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.delivery_days_max}</p>
                )}
              </div>
            </div>
            
            {/* Delivery Area */}
            <div>
              <Label>Area di consegna *</Label>
              <Select
                value={formData.delivery_area}
                onValueChange={(value) => setFormData({ ...formData, delivery_area: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_AREAS.map((area) => (
                    <SelectItem key={area.value} value={area.value}>
                      <span className="flex items-center gap-2">
                        <span>{area.icon}</span>
                        {area.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Altre aree (Europa, Mondo) saranno disponibili prossimamente
              </p>
            </div>
            
            {/* Free Shipping Threshold */}
            <div>
              <Label htmlFor="free-threshold">Soglia spedizione gratuita (€)</Label>
              <Input
                id="free-threshold"
                type="number"
                step="0.01"
                min="0"
                value={formData.free_shipping_threshold}
                onChange={(e) => setFormData({ ...formData, free_shipping_threshold: e.target.value })}
                placeholder="Lascia vuoto se non applicabile"
                className={formErrors.free_shipping_threshold ? 'border-red-500' : ''}
              />
              {formErrors.free_shipping_threshold && (
                <p className="text-sm text-red-500 mt-1">{formErrors.free_shipping_threshold}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Se il cliente raggiunge questo importo (nei tuoi prodotti), la spedizione sarà gratuita
              </p>
            </div>
            
            {/* Is Default */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="font-medium">Regola predefinita</Label>
                <p className="text-xs text-slate-500">Applicata automaticamente ai nuovi prodotti</p>
              </div>
              <Switch
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                disabled={rules.length === 0 && !editingRule}
              />
            </div>
            
            {/* Is Active */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="font-medium">Regola attiva</Label>
                <p className="text-xs text-slate-500">Disattiva temporaneamente senza eliminarla</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            
            {/* Combine Shipping */}
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div>
                <Label className="font-medium flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-600" />
                  Accorpa spedizioni
                </Label>
                <p className="text-xs text-slate-600 mt-1">
                  Se attivo, il cliente paga una sola spedizione per acquisti multipli dei tuoi prodotti.
                  Se disattivo, le spese si sommano per ogni prodotto.
                </p>
              </div>
              <Switch
                checked={formData.combine_shipping}
                onCheckedChange={(checked) => setFormData({ ...formData, combine_shipping: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#008080] text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {editingRule ? 'Salva Modifiche' : 'Crea Regola'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Elimina Regola</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la regola "{ruleToDelete?.name}"?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Elimina'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
